import type { DetectedHold } from '../../../types/climb-scan';
import type { BoardLedLine } from '../../../types/device';

/**
 * **LED lines drawn on a scanned wall** (docs/SPRAY_WALL_SCAN_PLAN.md).
 *
 * The holds are the scan's; a line says which LED lights which of them. A
 * click lands on the centre of the hold under it (or stays on bare wall, for a
 * turn between panels), a line takes every hold it crosses in the order it
 * meets them, a hold is taken once — by the first line reaching it — and never
 * one with no LED behind it. The strip is one run in the order the lines were
 * drawn, so a hold's LED is where its line starts plus how far along it the
 * hold sits.
 *
 * Everything here is in fractions of the uploaded photo (the scan's space), and
 * everything is affine-invariant — crossing and order along a segment — so the
 * photo's aspect ratio does not matter.
 *
 * The server keeps the same rules (Board#normalize_led_holds,
 * #scanned_led_starts) and is the authority; this is what the panel draws
 * while the lines are being drawn, and what it sends.
 */

export interface PhotoPoint {
  x: number;
  y: number;
}

/** A hold of the scan, by its place in `detected_holds`, outlined on the photo. */
export interface HoldShape {
  place: number;
  outline: PhotoPoint[];
  centre: PhotoPoint;
  area: number;
  box: { minX: number; minY: number; maxX: number; maxY: number };
}

/** The scan's outlines are on a 0-1000 grid of the photo. */
const GRID = 1000;

/** Points are stored to 5 decimals, as the server rounds them. */
const round = (value: number) => Math.round(value * 100000) / 100000;

function shapeOf(place: number, outline: PhotoPoint[]): HoldShape {
  let area = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i];
    const b = outline[(i + 1) % outline.length];
    const cross = a.x * b.y - b.x * a.y;
    area += cross;
    cx += (a.x + b.x) * cross;
    cy += (a.y + b.y) * cross;
  }
  area /= 2;
  const mean = {
    x: outline.reduce((sum, p) => sum + p.x, 0) / outline.length,
    y: outline.reduce((sum, p) => sum + p.y, 0) / outline.length,
  };
  // The centroid of the shape, or the mean of its points for a sliver whose
  // area is too small to divide by.
  const centre = Math.abs(area) > 1e-12 ? { x: cx / (6 * area), y: cy / (6 * area) } : mean;
  const xs = outline.map((p) => p.x);
  const ys = outline.map((p) => p.y);

  return {
    place,
    outline,
    centre,
    area: Math.abs(area),
    box: { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) },
  };
}

/** The holds at `places` of a scan's `detected_holds`, as shapes on the photo. */
export function holdShapes(detected: DetectedHold[], places: number[]): HoldShape[] {
  return places.flatMap((place) => {
    const outline = detected[place]?.outline;
    if (!outline || outline.length < 3) return [];

    return [shapeOf(place, outline.map(([x, y]) => ({ x: x / GRID, y: y / GRID })))];
  });
}

function inBox(shape: HoldShape, p: PhotoPoint) {
  const { box } = shape;
  return p.x >= box.minX && p.x <= box.maxX && p.y >= box.minY && p.y <= box.maxY;
}

/** Whether `p` is inside `outline` (even-odd rule). */
export function containsPoint(outline: PhotoPoint[], p: PhotoPoint): boolean {
  let inside = false;
  for (let i = 0, j = outline.length - 1; i < outline.length; j = i++) {
    const a = outline[i];
    const b = outline[j];
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/**
 * The hold a press landed on: the **smallest** one containing it, because a
 * hold bolted onto a volume sits inside the volume's outline and the press
 * meant the hold.
 */
export function holdAt(holds: HoldShape[], p: PhotoPoint): HoldShape | null {
  let best: HoldShape | null = null;
  for (const shape of holds) {
    if (!inBox(shape, p) || !containsPoint(shape.outline, p)) continue;
    if (!best || shape.area < best.area) best = shape;
  }
  return best;
}

/**
 * How far past a hold's edge, as a share of the photo, an end of a line is
 * still pulled onto it — the magnet's reach, so a small foothold is not a
 * pixel-perfect target, whatever the size of the hold.
 */
export const MAGNET_REACH = 0.015;

/**
 * **The holds are magnets**: an end of a line dropped on a hold, or near
 * enough to one (`MAGNET_REACH`), lands on its centre; on bare wall it stays
 * where it is — a turn between two panels. Used for a click and for every
 * step of a drag, so the end is seen to jump onto a hold as it nears it.
 */
export function snapPoint(holds: HoldShape[], p: PhotoPoint, reach = MAGNET_REACH): PhotoPoint {
  const hold = holdAt(holds, p) ?? nearestHold(holds, p, reach);
  const at = hold ? hold.centre : p;
  return { x: round(Math.min(1, Math.max(0, at.x))), y: round(Math.min(1, Math.max(0, at.y))) };
}

/**
 * The hold `p` is nearest to, measured to the edge of its box, within
 * `reach`; null past it. Two equally near — `p` between them — go to the one
 * whose centre is closer.
 */
function nearestHold(holds: HoldShape[], p: PhotoPoint, reach: number): HoldShape | null {
  let best: HoldShape | null = null;
  let bestEdge = reach;
  let bestCentre = Infinity;
  for (const shape of holds) {
    const { box } = shape;
    const dx = Math.max(box.minX - p.x, 0, p.x - box.maxX);
    const dy = Math.max(box.minY - p.y, 0, p.y - box.maxY);
    const edge = Math.hypot(dx, dy);
    if (edge > reach) continue;

    const centre = Math.hypot(shape.centre.x - p.x, shape.centre.y - p.y);
    if (edge < bestEdge || (edge === bestEdge && centre < bestCentre)) {
      best = shape;
      bestEdge = edge;
      bestCentre = centre;
    }
  }
  return best;
}

/** Whether two ends of lines are the same joint, and so move together. */
export function sameJoint(a: PhotoPoint | undefined, b: PhotoPoint | undefined): boolean {
  return !!a && !!b && Math.abs(a.x - b.x) < 1e-6 && Math.abs(a.y - b.y) < 1e-6;
}

/**
 * Moves one end of one line, carrying along the end of the line next to it
 * when the two are one joint: the lines are drawn as one run, and a corner
 * that came apart when dragged would leave a gap no hold sits in.
 */
export function moveLineEnd(
  lines: BoardLedLine[],
  lineIndex: number,
  pointIndex: number,
  to: PhotoPoint
): BoardLedLine[] {
  const line = lines[lineIndex];
  if (!line || !line.points[pointIndex]) return lines;

  const from = line.points[pointIndex];
  const isFirst = pointIndex === 0;
  const isLast = pointIndex === line.points.length - 1;
  const previous = lines[lineIndex - 1];
  const next = lines[lineIndex + 1];
  const joinedPrevious = isFirst && sameJoint(previous?.points.at(-1), from);
  const joinedNext = isLast && sameJoint(next?.points[0], from);

  return lines.map((current, at) => {
    if (at === lineIndex) {
      return { ...current, points: current.points.map((point, index) => (index === pointIndex ? to : point)) };
    }
    if (at === lineIndex - 1 && joinedPrevious) {
      return { ...current, points: current.points.map((point, index) => (index === current.points.length - 1 ? to : point)) };
    }
    if (at === lineIndex + 1 && joinedNext) {
      return { ...current, points: current.points.map((point, index) => (index === 0 ? to : point)) };
    }
    return current;
  });
}

/**
 * The ends of the lines, as the handles there are to take hold of: **one per
 * joint**, since a joint is the end of one line and the start of the next in
 * the same place, and two handles stacked on one hold is a coin toss over
 * which one a drag picks up.
 */
export function lineHandles(lines: BoardLedLine[]): Array<{ line: number; point: number; at: PhotoPoint }> {
  return lines.flatMap((line, index) =>
    line.points.flatMap((at, pointIndex) =>
      pointIndex === 0 && sameJoint(lines[index - 1]?.points.at(-1), at) ? [] : [{ line: index, point: pointIndex, at }]
    )
  );
}

function orientation(a: PhotoPoint, b: PhotoPoint, c: PhotoPoint) {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function segmentsCross(a: PhotoPoint, b: PhotoPoint, c: PhotoPoint, d: PhotoPoint) {
  const d1 = orientation(c, d, a);
  const d2 = orientation(c, d, b);
  const d3 = orientation(a, b, c);
  const d4 = orientation(a, b, d);
  return d1 * d2 <= 0 && d3 * d4 <= 0 && !(d1 === 0 && d2 === 0 && d3 === 0 && d4 === 0);
}

/** Whether the segment `a`–`b` touches the hold: an end inside, or an edge crossed. */
function segmentTouches(a: PhotoPoint, b: PhotoPoint, shape: HoldShape) {
  const { box } = shape;
  if (Math.max(a.x, b.x) < box.minX || Math.min(a.x, b.x) > box.maxX) return false;
  if (Math.max(a.y, b.y) < box.minY || Math.min(a.y, b.y) > box.maxY) return false;
  if (containsPoint(shape.outline, a) || containsPoint(shape.outline, b)) return true;

  const { outline } = shape;
  return outline.some((p, i) => segmentsCross(a, b, p, outline[(i + 1) % outline.length]));
}

/** The holds a segment crosses, in the order it meets them (by their centres). */
export function crossedHolds(a: PhotoPoint, b: PhotoPoint, holds: HoldShape[]): HoldShape[] {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = dx * dx + dy * dy;
  const along = (p: PhotoPoint) => (length === 0 ? 0 : ((p.x - a.x) * dx + (p.y - a.y) * dy) / length);

  return holds
    .filter((shape) => segmentTouches(a, b, shape))
    .sort((one, other) => along(one.centre) - along(other.centre));
}

/**
 * **The holds each line takes**, in strip order: every hold its segments cross,
 * once, by the first line to reach it, never one with no LED. `holds` are the
 * ones a line may take at all — the shown ones: a hold the setter hid is junk
 * or a foothold too small to matter, and has no LED to number.
 */
export function threadLines(lines: BoardLedLine[], holds: HoldShape[], unlit: ReadonlySet<number>): number[][] {
  const taken = new Set<number>();

  return lines.map((line) => {
    const places: number[] = [];
    for (let i = 0; i + 1 < line.points.length; i++) {
      for (const shape of crossedHolds(line.points[i], line.points[i + 1], holds)) {
        if (unlit.has(shape.place) || taken.has(shape.place)) continue;
        taken.add(shape.place);
        places.push(shape.place);
      }
    }
    return places;
  });
}

/** Where each line's LEDs start: its own number, else on from the line before. */
export function ledStarts(lines: BoardLedLine[], ledHolds: number[][]): number[] {
  let next = 0;
  return lines.map((line, index) => {
    const start = line.led_start ?? next;
    next = start + (ledHolds[index]?.length ?? 0);
    return start;
  });
}

/** Every taken hold's LED, by place. */
export function ledByPlace(lines: BoardLedLine[], ledHolds: number[][]): Map<number, number> {
  const starts = ledStarts(lines, ledHolds);
  const leds = new Map<number, number>();
  ledHolds.forEach((places, index) => places.forEach((place, offset) => leds.set(place, starts[index] + offset)));
  return leds;
}

/**
 * **A joint added where a line crosses a hold** ("Add a joint"): the line is
 * cut in two at the hold's centre, the halves sharing the new joint, so the
 * line can be bent there by dragging it. The hold is the first half's last,
 * and the second half follows on, so no LED moves. Null when the hold is no
 * line's, or is already a joint.
 */
export function splitLineAt(
  lines: BoardLedLine[],
  ledHolds: number[][],
  holds: HoldShape[],
  place: number
): { lines: BoardLedLine[]; line: number } | null {
  const lineIndex = ledHolds.findIndex((places) => places.includes(place));
  const hold = holds.find((shape) => shape.place === place);
  const line = lines[lineIndex];
  if (!line || !hold) return null;

  const at = { x: round(hold.centre.x), y: round(hold.centre.y) };
  if (line.points.some((point) => sameJoint(point, at))) return null;

  for (let i = 0; i + 1 < line.points.length; i++) {
    if (!crossedHolds(line.points[i], line.points[i + 1], [hold]).length) continue;

    const before: BoardLedLine = { points: [...line.points.slice(0, i + 1), at], led_start: line.led_start };
    const after: BoardLedLine = { points: [at, ...line.points.slice(i + 1)], led_start: null };
    return { lines: [...lines.slice(0, lineIndex), before, after, ...lines.slice(lineIndex + 1)], line: lineIndex };
  }
  return null;
}

/**
 * How far apart two holds may be, as a share of the photo, and still count as
 * touching: the detector often leaves a sliver between two pieces of one hold.
 */
export const TOUCH_GAP = 0.004;

function distanceToSegment(p: PhotoPoint, a: PhotoPoint, b: PhotoPoint) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = dx * dx + dy * dy;
  const t = length === 0 ? 0 : Math.min(1, Math.max(0, ((p.x - a.x) * dx + (p.y - a.y) * dy) / length));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/**
 * **Whether two holds touch** — overlap, or come within `gap` of each other:
 * two pieces of one hold the detector cut in two, which the panel offers to
 * join.
 */
export function holdsTouch(one: HoldShape, other: HoldShape, gap = TOUCH_GAP): boolean {
  const { box: a } = one;
  const { box: b } = other;
  if (a.minX - gap > b.maxX || b.minX - gap > a.maxX || a.minY - gap > b.maxY || b.minY - gap > a.maxY) return false;

  // One inside the other, or their edges crossing.
  if (containsPoint(one.outline, other.outline[0]) || containsPoint(other.outline, one.outline[0])) return true;
  const edges = (shape: HoldShape) =>
    shape.outline.map((point, i) => [point, shape.outline[(i + 1) % shape.outline.length]] as const);
  for (const [p, q] of edges(one)) {
    for (const [r, s] of edges(other)) if (segmentsCross(p, q, r, s)) return true;
  }

  // Or a sliver apart.
  const near = (from: HoldShape, to: HoldShape) =>
    from.outline.some((point) => edges(to).some(([r, s]) => distanceToSegment(point, r, s) <= gap));
  return near(one, other) || near(other, one);
}
